import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { Link, useRouteMatch, useLocation, useHistory } from 'react-router-dom';
import { useDebouncedValue, useToggle } from '../../../libs/react-use-extra';
import { useFullLoader, useToast } from '../../../redux/actions/UI';
import {
  useChangeStatusOfSelectedCampaignsMutation,
  useCampaignsQuery,
} from '../queries';
import CampaignView from '../CampaignView';
import CampaignsTable from '../CampaignsTable';
import {
  ArchiveList,
  CreateButton,
  Header,
  SearchInput,
  Tab,
  TableWrapper,
  InfoBubble,
  InfoBubbleWrapper,
  ChangeStatusDropdown,
} from './CampaignsIndex.styled';
import { OptionContent } from '../StatusPicker/StatusPicker.styled';
import { Close, InfoOutlined } from '@material-ui/icons';
import ConfirmationDialog from '../ConfirmationDialog';
import Campaign from '../domain/campaign';
import {
  AutomatedCampaignIcon,
  ChangeStattusIcon,
  ActiveIcon,
  PausedIcon,
  ArchiveIcon,
} from '../SVGs/Icons';
import PaperAirplaneAnimation from '../PaperAirplaneAnimation';

const INITIAL_PAGINATION = {
  current: 1,
  size: CampaignsTable.DEFAULT_PAGE_SIZE,
};

const SEARCH_DEBOUNCE = 500;

const INITIAL_STATUSES_FILTER = ['active', 'paused', 'unfinished'];

const DEFAULT_SORTING = {
  field: 'updatedAt',
  direction: 'desc',
};

const STATUS_MAPPING = {
  Active: 'activate',
  Archived: 'archive',
  Paused: 'pause',
};

const STATUS_MAPPING_FOR_CONFIRMATION = {
  Active: 'Activating',
  Archived: 'Archiving',
  Paused: 'Pausing',
};

const STATUS_MAPPING_FOR_API = {
  Active: 'active',
  Archived: 'archived',
  Paused: 'paused',
};

const CampaignsIndex = () => {
  const { errorToast } = useToast();
  const fullLoader = useFullLoader();
  const location = useLocation();
  const history = useHistory();
  const confettiAnimationRef = useRef(null);

  const [statuses, setStatuses] = useState(INITIAL_STATUSES_FILTER);
  const [changeStatus, setChangeStatus] = useState('');

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [sortBy, setSortBy] = useState(DEFAULT_SORTING);
  const [total, setTotal] = useState(0);

  const [openedCampaignId, setOpenedCampaignId] = useState(null);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [
    isChangeStatusConfirmationOpen,
    { on: openStatusChangeConfirmation, off: closeStatusChangeConfirmation },
  ] = useToggle();
  const [isInfoExpanded, setIsInfoExpanded] = useState(() => {
    const stored = localStorage.getItem('ac_index_info_expanded');
    return stored === null ? true : stored === 'true';
  });

  const handleSetInfoExpanded = value => {
    setIsInfoExpanded(value);
    localStorage.setItem('ac_index_info_expanded', String(value));
  };

  const haveVisibleStatuses = statuses.length > 0;
  const { data, isLoading, error } = useCampaignsQuery(
    {
      page: pagination.current,
      pageSize: pagination.size,
      sortBy: sortBy?.field,
      sortDirection: sortBy?.direction,
      search: debouncedSearch,
      statuses,
    },
    {
      enabled: haveVisibleStatuses,
      onSuccess(data) {
        setTotal(data.total);
      },
      onError() {
        errorToast('Failed to load campaigns. Please, try again later.');
      },
    }
  );

  // Trigger confetti celebration when page loads after finishing with active status
  const triggerConfettiCelebration = useCallback(() => {
    if (!confettiAnimationRef.current) return;

    const containerWidth = window.innerWidth;
    const containerHeight = window.innerHeight;

    // First blast: left side (lowered a bit)
    setTimeout(() => {
      confettiAnimationRef.current.triggerExplosion(
        containerWidth * 0.15,
        containerHeight * 0.45
      );
    }, 0);

    // Second blast: right side (lowered a bit, quickly after first)
    setTimeout(() => {
      confettiAnimationRef.current.triggerExplosion(
        containerWidth * 0.85,
        containerHeight * 0.45
      );
    }, 200);

    // Third blast: top center
    setTimeout(() => {
      confettiAnimationRef.current.triggerExplosion(
        containerWidth * 0.5,
        containerHeight * 0.15
      );
    }, 400);
  }, []);

  // Check if we should trigger confetti after page loads
  useEffect(() => {
    // Only trigger if:
    // 1. Location state indicates confetti should trigger
    // 2. Page is not loading (data has loaded)
    if (location.state?.triggerConfetti && !isLoading && data) {
      // Small delay to ensure page is fully rendered
      setTimeout(() => {
        triggerConfettiCelebration();
        // Clear the state to prevent retriggering on re-renders
        // This ensures we don't trigger again if the effect runs multiple times
        history.replace(location.pathname, {});
      }, 100);
    }
  }, [location.state, isLoading, data, history, location.pathname]);

  const selectedCampaigns = useMemo(() => {
    if (!data) {
      return [];
    }

    return selectedCampaignIds.map(id =>
      data.campaigns.find(campaign => campaign._id === id)
    );
  }, [data, selectedCampaignIds]);

  const { mutateAsync } = useChangeStatusOfSelectedCampaignsMutation();

  const rows = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.campaigns.map(campaign => {
      return {
        id: campaign._id,
        name: campaign.name,
        description: campaign.description,
        openRate: Campaign.calculateAggregateOpenRate(campaign.stages),
        clickRate: Campaign.calculateAggregateClickRate(campaign.stages),
        status: campaign.status,
        updatedAt: campaign.updatedAt,
      };
    });
  }, [data]);

  const handleChange = ({ pagination, sortBy }) => {
    setPagination(pagination);
    setSortBy(previousSortBy => {
      if (sortBy) {
        if (typeof sortBy === 'function') {
          return sortBy(previousSortBy);
        } else {
          return sortBy;
        }
      }

      if (previousSortBy.field === 'updatedAt') {
        return {
          field: 'updatedAt',
          direction: previousSortBy.direction === 'asc' ? 'desc' : 'asc',
        };
      }

      return DEFAULT_SORTING;
    });
  };

  const handleSearch = event => {
    setSearch(event.target.value);
    setTotal(0);
  };

  const handleStatusChange = newStatuses => {
    setStatuses(newStatuses);
    setTotal(0);
  };

  const closeCampaign = () => {
    setOpenedCampaignId(null);
  };

  const hasNoSelectedCampaigns = selectedCampaignIds.length === 0;

  const changeStatusOfSelectedCampaigns = async () => {
    const targetStatus = STATUS_MAPPING_FOR_API[changeStatus];

    // Filter out campaigns that already have the target status
    const campaignsToUpdate = selectedCampaigns.filter(
      campaign => campaign.status !== targetStatus
    );

    // If no campaigns need updating, skip the API call
    if (campaignsToUpdate.length === 0) {
      setSelectedCampaignIds([]);
      setChangeStatus('');
      closeStatusChangeConfirmation();
      return;
    }

    // Close confirmation dialog before showing loader
    closeStatusChangeConfirmation();

    const idsToUpdate = campaignsToUpdate.map(campaign => campaign._id);

    try {
      await fullLoader.during(
        `${STATUS_MAPPING_FOR_CONFIRMATION[changeStatus]} selected campaigns...`,
        () =>
          mutateAsync({
            ids: idsToUpdate,
            status: targetStatus,
          })
      );
      setSelectedCampaignIds([]);
      setChangeStatus('');
    } catch (error) {
      // Error is handled by the mutation's onError callback
      // Reset state even on error
      setChangeStatus('');
    }
  };

  const route = useRouteMatch();

  return (
    <>
      <Header>
        <Header.Left>
          <AutomatedCampaignIcon />
          <Tab>
            Automated Campaigns{' '}
            <InfoOutlined
              onClick={() => handleSetInfoExpanded(!isInfoExpanded)}
              style={{
                width: '20px',
                marginLeft: '3px',
                cursor: 'pointer',
                color: '#9B9B9B',
              }}
            />
          </Tab>
        </Header.Left>
        <Header.Right>
          <TableWrapper.Toolbar>
            <SearchInput
              value={search}
              onChange={handleSearch}
              placeholder="Search"
            />
            <ChangeStatusDropdown>
              <ChangeStatusDropdown.Select
                variant="outlined"
                size="small"
                value={changeStatus}
                onChange={e => {
                  setChangeStatus(e.target.value);
                  openStatusChangeConfirmation();
                }}
                displayEmpty
                disabled={hasNoSelectedCampaigns}
                renderValue={selected => {
                  return (
                    <ChangeStatusDropdown.ValueContainer>
                      <ChangeStattusIcon
                        fill={
                          selectedCampaignIds.length > 0 ? '#1990ff' : '#1F1F1F'
                        }
                      />
                      {isChangeStatusConfirmationOpen
                        ? selected
                        : 'Change Status'}
                    </ChangeStatusDropdown.ValueContainer>
                  );
                }}
              >
                <ChangeStatusDropdown.MenuItem value="Active">
                  <OptionContent>
                    <ActiveIcon /> Active
                  </OptionContent>
                </ChangeStatusDropdown.MenuItem>
                <ChangeStatusDropdown.MenuItem value="Paused">
                  <OptionContent>
                    <PausedIcon /> Paused
                  </OptionContent>
                </ChangeStatusDropdown.MenuItem>
                <ChangeStatusDropdown.MenuItem value="Archived">
                  <OptionContent>
                    <ArchiveIcon /> Archived
                  </OptionContent>
                </ChangeStatusDropdown.MenuItem>
              </ChangeStatusDropdown.Select>
            </ChangeStatusDropdown>
          </TableWrapper.Toolbar>
          {isChangeStatusConfirmationOpen && (
            <ConfirmationDialog
              message={
                <>
                  <span>
                    Are you sure you want to {STATUS_MAPPING[changeStatus]} all
                    these campaigns?
                  </span>
                  <ArchiveList>
                    {selectedCampaigns.map(campaign => (
                      <ArchiveList.Item key={campaign._id}>
                        {campaign.name}
                      </ArchiveList.Item>
                    ))}
                  </ArchiveList>
                </>
              }
              onConfirm={changeStatusOfSelectedCampaigns}
              onClose={() => {
                setChangeStatus('');
                closeStatusChangeConfirmation();
              }}
            />
          )}
          <CreateButton to={`${route.url}/new`} component={Link}>
            New Campaign
          </CreateButton>
        </Header.Right>
      </Header>
      <TableWrapper>
        <InfoBubbleWrapper isVisible={isInfoExpanded}>
          <InfoBubble>
            <InfoBubble.Content>
              <InfoBubble.Text>
                Automated Campaigns let you send messages automatically based on
                specific events. <br />
                Examples: Send a birthday message automatically, Welcome new
                contacts with a message series. <br /> A Campaign is made up of
                an Audience and one or more Stages. <br /> • The Audience
                defines who receives the messages. <br /> • The Stages define
                what messages are sent and when.
              </InfoBubble.Text>
              <InfoBubble.CloseButton
                onClick={() => handleSetInfoExpanded(false)}
              >
                <Close />
              </InfoBubble.CloseButton>
            </InfoBubble.Content>
          </InfoBubble>
        </InfoBubbleWrapper>
        <TableWrapper.Content>
          <CampaignsTable
            data={haveVisibleStatuses ? rows : []}
            total={haveVisibleStatuses ? total : 0}
            pagination={pagination}
            onChange={handleChange}
            isLoading={isLoading}
            error={error}
            sortBy={sortBy}
            onCampaignClick={setOpenedCampaignId}
            selected={selectedCampaignIds}
            onSelectionChange={setSelectedCampaignIds}
            noResultsText={
              debouncedSearch
                ? 'No results found.'
                : haveVisibleStatuses
                ? "You don't have any campaigns yet."
                : 'You have not selected any status in the filter'
            }
            hasSearchTerm={!!debouncedSearch}
            statuses={statuses}
            onStatusChange={handleStatusChange}
          />
        </TableWrapper.Content>
      </TableWrapper>
      {openedCampaignId && (
        <CampaignView id={openedCampaignId} onClose={closeCampaign} />
      )}
      {/* 
        PaperAirplaneAnimation configured for manual triggering only.
        Explosions will be triggered programmatically via triggerExplosion() ref method.
      */}
      <PaperAirplaneAnimation
        ref={confettiAnimationRef}
        manualOnly={true}
        speed={2}
        confettiCount={70}
        airplaneCount={30}
      />
    </>
  );
};

export default CampaignsIndex;
